import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { EventCard } from "@/components/EventCard";
import { LandingHero } from "@/components/LandingHero";
import { CategoryCarousel } from "@/components/CategoryCarousel";
import { CityCarousel } from "@/components/CityCarousel";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buyerFAQItems } from "@/data/faq-data";
import { lowestTicketTotal } from "@/lib/ticket-pricing";

export default function Index() {
  const { t } = useTranslation("landingHome");
  const { user, role, loading } = useAuth();

  const { data: featuredEvents = [], isLoading: loadingEvents } = useQuery({
    queryKey: ["featured-events"],
    queryFn: async () => {
      const query = supabase
        .from("events")
        .select("*, profiles!events_producer_id_fkey(organizer_slug), organizations(slug)")
        .eq("status", "published")
        .eq("visibility", "public")
        .eq("is_featured", true)
        .gte("end_date", new Date().toISOString())
        .order("start_date", { ascending: true })
        .limit(6);

      const { data, error } = await query;
      if (error) throw error;

      const eventIds = (data || []).map(e => e.id);
      const { data: allTiers } = await supabase
        .from("ticket_tiers")
        .select("event_id, price, is_visible, name, absorb_fee")
        .in("event_id", eventIds);
        
      const tiersByEvent = (allTiers || []).reduce((acc: Record<string, any[]>, tier: any) => {
        if (!acc[tier.event_id]) acc[tier.event_id] = [];
        acc[tier.event_id].push(tier);
        return acc;
      }, {});

      return (data || []).map((e: any) => {
        const tiers = tiersByEvent[e.id] || [];
        const validTiers = tiers.filter((t: any) => 
          t.is_visible !== false && 
          t.name !== "Lista de Convidado" &&
          t.price !== null
        );
        
        const minPrice = lowestTicketTotal(validTiers, e.platform_fee_percent);
        
        const { ticket_tiers, ...rest } = e;
        return { ...rest, min_price: minPrice };
      });
    },
  });

  // Redirect admin/producer to their dashboards; buyers stay on landing
  if (!loading && user && role) {
    if (role === "admin") return <Navigate to="/admin/relatorios" replace />;
    if (role === "producer") return <Navigate to="/produtor/dashboard" replace />;
    // Buyers can see the landing page
  }

  return (
    <>
      <LandingHero />

      {/* ===== CATEGORY CAROUSEL ===== */}
      <CategoryCarousel className="bg-secondary/30" />

      {/* ===== FEATURED EVENTS ===== */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-display text-2xl lg:text-3xl font-bold">{t("featuredEvents.title")}</h2>
              <p className="text-muted-foreground mt-1">{t("featuredEvents.subtitle")}</p>
            </div>
            <Button variant="ghost" asChild className="hidden sm:inline-flex">
              <Link to="/eventos">{t("featuredEvents.viewAll")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>

          {loadingEvents ? (
            <LoadingSkeleton variant="card" count={3} />
          ) : featuredEvents.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.map((event: any, index: number) => (
                <EventCard
                  key={event.id}
                  title={event.title}
                  date={format(new Date(event.start_date), "dd MMM yyyy · HH'h'mm", { locale: ptBR })}
                  city={event.venue_city || "Online"}
                  imageUrl={event.cover_image_url}
                  priceFrom={event.min_price ?? 0}
                  category={event.category}
                  slug={event.slug}
                  producerSlug={event.organizations?.slug || event.profiles?.organizer_slug}
                  isOrg={!!event.organization_id && !!event.organizations?.slug}
                  eventId={event.id}
                  priority={index === 0}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-12">{t("featuredEvents.empty")}</p>
          )}

          <div className="mt-6 text-center sm:hidden">
            <Button variant="outline" asChild>
              <Link to="/eventos">{t("featuredEvents.viewAllMobile")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ===== CITY CAROUSEL ===== */}
      <CityCarousel className="bg-secondary/30" />

      {/* ===== CTA PRODUTOR ===== */}
      <section className="py-16 lg:py-24">
        <div className="container text-center space-y-6">
          <h2 className="font-display text-2xl lg:text-3xl font-bold">{t("producerCta.title")}</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            {t("producerCta.description")}
          </p>
          <Button variant="default" size="lg" asChild>
            <Link to="/produtores">
              {t("producerCta.button")} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container max-w-2xl">
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-center mb-8">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {buyerFAQItems.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-lg px-4">
                <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
